import { Fragment, useEffect, useRef, useState } from "react";
import { tableViewGet } from "../service/api-service";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { FilterMatchMode, SortOrder } from "primereact/api";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { GenericTableColumTypes, GenericTableFilterTypes } from "../enum/TableView";
import { TriStateCheckbox } from "primereact/tristatecheckbox";
import {
  GenericTableColumns,
  GenericTableFilterOption,
  GenericTableProps,
} from "../models/GenericTableViewModel";
import { ContextMenu } from "primereact/contextmenu";
import dayjs from "dayjs";
import { Toast } from "primereact/toast";
import { Stack } from "@mui/material";
import { FormControl } from "@mui/joy";
import { Tooltip } from "primereact/tooltip";
import { Avatar } from "primereact/avatar";
import { generateExcelFile } from "../service/excelService";
import GenericTableDynamicBody from "./GenericTableDynamicBody";

function camelize(str: string) {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, "");
}

export default function GenericTableView(tableProps: GenericTableProps) {
  let baseFilters: any = {};

  tableProps.columns.forEach(c => {
    baseFilters[c.field] = { value: null, matchMode: FilterMatchMode.CONTAINS };
  });

  const tempFilter = { ...baseFilters, ...tableProps.baseFilters };

  const baseFilter = {
    ...{
      rows: 50, // Default page size for pagination
      page: 0,
      globalSearch: "",
      sortField: tableProps.sortByField || "created_at",
      sortOrder: SortOrder.DESC,
      filters: tableProps.baseFilters,
      col: tableProps.columns
        .filter(c => c.filter && !!c.field && c.field !== "" && c.field !== " ")
        .map(c => c.field),
    },
    ...(tableProps.queryParams || {}),
  };

  const [loading, setLoading] = useState(false);
  const [initLoad, setInitLoad] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [data, setData] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const cm = useRef(null as any);
  const dt = useRef(null as any); // Reference to DataTable
  const [filters, setFilters] = useState(tempFilter);
  const [globalSearch, setGlobalSearch] = useState("");
  const [lazyParams, setLazyParams] = useState(baseFilter);
  const toast = useRef(null as any);

  useEffect(() => {
    if (initLoad) return;
    loadLazyData();
  }, [lazyParams]);

  useEffect(() => {
    if (initLoad) return;
    loadLazyData(tableProps.reloadDelay);
  }, [tableProps.reload]);

  const loadLazyData = (reloadDelay?: number, retryCount = 0) => {
    if (loading) return;

    if (initLoad) setInitLoad(false);

    setLoading(true);

    const executeLoad = () => {
      tableViewGet(tableProps.url, lazyParams)
        .then(response => {
          const newData = response.content || [];
          const totalRecords = response.totalRecords || 0;

          setTotalRecords(totalRecords);
          if (tableProps.selectionKey) {
            const selectedData = newData[tableProps.selectionKey];
            setData(selectedData);
          } else {
            setData(newData);
          }

          tableProps.callbackOnLoad?.(newData);
        })
        .catch(error => {
          console.error("Error loading table data:", error);

          // Retry logic for network errors
          if (retryCount < 2 && (error.name === "NetworkError" || error.code === "NETWORK_ERROR")) {
            console.log(`Retrying request... Attempt ${retryCount + 1}`);
            setTimeout(
              () => {
                loadLazyData(reloadDelay, retryCount + 1);
              },
              1000 * (retryCount + 1)
            ); // Exponential backoff
            return;
          }

          setTotalRecords(0);
          setData([]);

          // Show error message to user
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to load data. Please try again.",
            life: 3000,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    };

    if (reloadDelay) {
      setTimeout(executeLoad, reloadDelay);
    } else {
      executeLoad();
    }
  };

  useEffect(() => {
    loadLazyData();
  }, []);

  // Load more data when scrolling to bottom
  const onPage = (event: any) => {
    setLazyParams((parm: any) => ({ ...parm, ...event }));
  };

  const onSort = (event: any) => {
    event["page"] = 0;
    setData([]); // Clear existing data when sorting
    setLazyParams((parm: any) => ({ ...parm, ...event }));
  };

  const onFilter = (event: any) => {
    event["page"] = 0;
    setData([]); // Clear existing data when filtering
    setLazyParams((parm: any) => ({ ...parm, ...event }));
  };

  const onGlobalSearch = (event: any) => {
    setGlobalSearch(event.target.value);
  };

  const onGlobalSearchEnter = (event: any) => {
    if (event.key === "Enter") {
      lazyParams["globalSearch"] = globalSearch;
      setData([]); // Clear existing data when searching
      setLazyParams(lazyParams);
      loadLazyData();
    }
  };

  const clearFilter = (event: any) => {
    setLazyParams(baseFilter);
    setFilters(tableProps.baseFilters);
    setGlobalSearch("");
    setData([]); // Clear existing data when clearing filters
  };

  const selectRow = (event: any) => {
    console.log("selectRow event == ", event);
    if (!event?.value?.id) return;
    tableProps.itemSelect?.(event);
    // cm?.current?.show(event.originalEvent.nativeEvent);
    setSelectedRow(event);
  };

  const contextSelectRow = (event: any) => {
    tableProps.contextItemSelected?.(event);
    setSelectedRow(event.value);
  };

  const exportButton = (row: any) => {
    generateExcelFile(
      `${tableProps.exportName}_${dayjs().format("YYYY-MM-DD")}`,
      data,
      tableProps.columns.map(c => ({ header: c.header, field: c.field }))
    );
  };

  const setActionColumnBody = (rowData: any) => {
    if (!tableProps.contextItems) return;
    return (
      <i
        className="pi pi-cog"
        onClick={e => {
          e?.preventDefault();
          contextSelectRow({
            value: rowData,
          });
          cm?.current?.show(e.nativeEvent);
        }}
      ></i>
    );
  };

  const renderHeader = () => {
    return (
      <div className="justify-content-between">
        <span className="p-input-icon-left">
          <i className="pi pi-search" style={{ paddingLeft: 10, paddingRight: 10 }} />
          <InputText
            style={{ paddingLeft: 30, fontSize: "small" }}
            value={globalSearch}
            className="p-inputtext-sm block mb-2"
            onChange={e => onGlobalSearch(e)}
            onKeyDown={onGlobalSearchEnter}
            placeholder="Global Search"
          />
        </span>
        <Button
          type="button"
          icon="pi pi-filter-slash"
          label="Clear"
          className="p-button-outlined"
          size="small"
          style={{ float: "right", fontSize: "small" }}
          onClick={clearFilter}
        />
        <Button
          type="button"
          icon="pi pi-refresh"
          label="Refresh"
          className="p-button-outlined"
          size="small"
          style={{ float: "right", fontSize: "small", marginRight: 10, color: "#ffc409" }}
          onClick={() => loadLazyData()}
        />
        {tableProps.secondAddButton ? (
          <Button
            type="button"
            icon="pi pi-plus"
            label="Add New"
            className="p-button-outlined"
            size="small"
            style={{ float: "right", fontSize: "small", marginRight: 10, color: "#2dd36f" }}
            onClick={tableProps.secondAddButton}
          />
        ) : (
          ""
        )}
        {tableProps.addButton ? (
          <Button
            type="button"
            icon="pi pi-plus"
            label="Add"
            className="p-button-outlined"
            size="small"
            style={{ float: "right", fontSize: "small", marginRight: 10, color: "#2dd36f" }}
            onClick={tableProps.addButton}
          />
        ) : (
          ""
        )}
        {tableProps.exportName && (
          <Button
            type="button"
            icon="pi pi-file-excel"
            label="Export"
            className="p-button-outlined"
            size="small"
            style={{ float: "right", fontSize: "small", marginRight: 10, color: "#6030ff" }}
            onClick={tableProps.exportButton || exportButton}
          />
        )}
        {tableProps.downloadButton && (
          <Button
            type="button"
            icon="pi pi-download"
            label="Download"
            className="p-button-outlined"
            size="small"
            style={{ float: "right", fontSize: "small", marginRight: 10, color: "#6030ff" }}
            onClick={tableProps.downloadButton}
          />
        )}
        {tableProps.importButton ? (
          <Button
            type="button"
            icon="pi pi-file-import"
            label="Import"
            className="p-button-outlined"
            size="small"
            style={{ float: "right", fontSize: "small", marginRight: 10, color: "#1E1A34" }}
            onClick={tableProps.importButton}
          />
        ) : (
          ""
        )}
      </div>
    );
  };

  return (
    <Fragment>
      <div>
        {tableProps.contextItems && tableProps.contextItems().length > 0 && (
          <ContextMenu model={tableProps.contextItems()} ref={cm} />
        )}
        <div
          className="card"
          style={{
            height: "calc(100vh - 300px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <DataTable
            value={data}
            ref={dt}
            lazy
            removableSort
            responsiveLayout="scroll"
            dataKey={tableProps.dataKey || "id"}
            size="small"
            totalRecords={totalRecords}
            paginator
            rows={lazyParams.rows}
            rowsPerPageOptions={[50, 100, 200, 500]}
            paginatorTemplate={
              "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            }
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
            first={lazyParams.page * lazyParams.rows}
            onPage={onPage}
            onSort={onSort}
            sortField={lazyParams.sortField}
            sortOrder={lazyParams.sortOrder}
            scrollable
            scrollHeight="calc(100vh - 350px)"
            filterDisplay={tableProps.filterDisplay || "menu"}
            onFilter={onFilter}
            filters={filters}
            loading={loading}
            resizableColumns
            columnResizeMode="expand"
            showGridlines
            header={renderHeader}
            selectionMode="single"
            selection={selectedRow}
            onRowClick={e => selectRow({ value: e.data })}
            emptyMessage={
              loading ? (
                <div
                  className="flex align-items-center justify-content-center"
                  style={{ padding: "2rem" }}
                >
                  <i
                    className="pi pi-spin pi-spinner"
                    style={{ fontSize: "1.5rem", marginRight: "0.5rem" }}
                  ></i>
                  <span>Loading data...</span>
                </div>
              ) : (
                "No records found."
              )
            }
            contextMenuSelection={selectedRow as any}
            onContextMenuSelectionChange={tableProps.contextItems ? contextSelectRow : undefined}
            onContextMenu={
              tableProps.contextItems ? e => cm?.current?.show(e.originalEvent) : undefined
            }
            style={{
              flex: "1",
              display: "flex",
              flexDirection: "column",
            }}
            tableStyle={{
              height: "100%",
            }}
          >
            {tableProps.contextItems && (
              <Column
                style={{ textAlign: "center", width: 160 }}
                alignFrozen="right"
                frozen={true}
                body={e => setActionColumnBody(e)}
              />
            )}

            {GenericTableDynamicBody({
              columns: tableProps.columns,
              toast,
            })}
          </DataTable>
        </div>
      </div>

      <Toast ref={toast} />
    </Fragment>
  );
}
